import { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Button, ButtonGroup, Divider, IconButton, Popover, Tooltip } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  Title,
  EmojiEmotions,
} from '@mui/icons-material';
import EmojiPicker from '../emails/EmojiPicker';

const SimpleTextEditor = ({ value, onChange, placeholder = 'Text eingeben...' }) => {
  const editorRef = useRef(null);
  const selectionRangeRef = useRef(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);

  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const formatHeading = level => {
    execCommand('formatBlock', `h${level}`);
  };

  const formatList = ordered => {
    execCommand(ordered ? 'insertOrderedList' : 'insertUnorderedList');
  };

  const saveSelectionRange = useCallback(() => {
    const editorEl = editorRef.current;
    if (!editorEl) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const containerNode = container?.nodeType === Node.TEXT_NODE ? container.parentNode : container;

    if (containerNode && editorEl.contains(containerNode)) {
      selectionRangeRef.current = range;
    }
  }, []);

  const restoreSelectionRange = useCallback(() => {
    const range = selectionRangeRef.current;
    const editorEl = editorRef.current;
    if (!range || !editorEl) return;

    const selection = window.getSelection();
    if (!selection) return;

    const container = range.commonAncestorContainer;
    const containerNode = container?.nodeType === Node.TEXT_NODE ? container.parentNode : container;
    if (containerNode && !editorEl.contains(containerNode)) return;

    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  const handleEmojiClick = useCallback(
    event => {
      saveSelectionRange();
      setEmojiAnchorEl(event.currentTarget);
    },
    [saveSelectionRange]
  );

  const handleEmojiClose = useCallback(() => {
    setEmojiAnchorEl(null);
  }, []);

  const handleEmojiSelect = useCallback(
    emoji => {
      editorRef.current?.focus();
      restoreSelectionRange();

      const inserted = document.execCommand('insertText', false, emoji);
      if (!inserted) {
        document.execCommand('insertHTML', false, emoji);
      }

      handleInput();
      handleEmojiClose();
    },
    [handleEmojiClose, handleInput, restoreSelectionRange]
  );

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          padding: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.default',
        }}
      >
        <ButtonGroup size='small' variant='outlined'>
          <Button onClick={() => formatHeading(1)} title='Überschrift 1'>
            <Title fontSize='small' />
            <Box component='span' sx={{ ml: 0.5, fontSize: '0.75rem' }}>
              1
            </Box>
          </Button>
          <Button onClick={() => formatHeading(2)} title='Überschrift 2'>
            <Title fontSize='small' />
            <Box component='span' sx={{ ml: 0.5, fontSize: '0.75rem' }}>
              2
            </Box>
          </Button>
          <Button onClick={() => formatHeading(3)} title='Überschrift 3'>
            <Title fontSize='small' />
            <Box component='span' sx={{ ml: 0.5, fontSize: '0.75rem' }}>
              3
            </Box>
          </Button>
        </ButtonGroup>
        <Divider orientation='vertical' flexItem />
        <ButtonGroup size='small' variant='outlined'>
          <Button onClick={() => execCommand('bold')} title='Fett'>
            <FormatBold fontSize='small' />
          </Button>
          <Button onClick={() => execCommand('italic')} title='Kursiv'>
            <FormatItalic fontSize='small' />
          </Button>
        </ButtonGroup>
        <Divider orientation='vertical' flexItem />
        <ButtonGroup size='small' variant='outlined'>
          <Button onClick={() => formatList(false)} title='Aufzählungsliste'>
            <FormatListBulleted fontSize='small' />
          </Button>
          <Button onClick={() => formatList(true)} title='Nummerierte Liste'>
            <FormatListNumbered fontSize='small' />
          </Button>
        </ButtonGroup>

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
          <Tooltip title='Emoji'>
            <IconButton size='small' onClick={handleEmojiClick}>
              <EmojiEmotions fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>

        <Popover
          open={Boolean(emojiAnchorEl)}
          anchorEl={emojiAnchorEl}
          onClose={handleEmojiClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <EmojiPicker onEmojiSelect={handleEmojiSelect} />
        </Popover>
      </Box>
      <Box
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onMouseUp={saveSelectionRange}
        onKeyUp={saveSelectionRange}
        suppressContentEditableWarning
        sx={{
          minHeight: '200px',
          maxHeight: '500px',
          overflowY: 'auto',
          padding: 2,
          outline: 'none',
          '&:empty:before': {
            content: `"${placeholder}"`,
            color: 'text.disabled',
          },
          '& h1': {
            fontSize: '2rem',
            fontWeight: 600,
            margin: '0.5em 0',
          },
          '& h2': {
            fontSize: '1.5rem',
            fontWeight: 600,
            margin: '0.5em 0',
          },
          '& h3': {
            fontSize: '1.25rem',
            fontWeight: 600,
            margin: '0.5em 0',
          },
          '& ul, & ol': {
            margin: '0.5em 0',
            paddingLeft: '2em',
          },
          '& p': {
            margin: '0.5em 0',
          },
        }}
      />
    </Box>
  );
};

export default SimpleTextEditor;

